/**
 * AdjusterQABot - AI-powered Q&A sidebar for claim analysis
 * 
 * This component provides a chat interface where users can ask
 * questions about the adjuster analysis results.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  MessageCircle,
  Send,
  Loader2,
  Trash2,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  HelpCircle,
  BookOpen
} from 'lucide-react';
// 2026-04-29 (TS-FU3 batch 3): was `import api from '../services/api'`,
// which exports only domain-specific helpers (getOrganization,
// getReports, getReport, generateReport) — NOT the generic `get/post/
// delete` methods used below. Calls failed with TypeError at runtime,
// silently swallowed by the try/catch around them, so loadHistory(),
// handleSend(), and handleClearHistory() were no-ops. PR #231 toggle
// fix exposed the latent bug by making the bot reachable. Switched
// to `utils/api` which has the generic methods. apiFetch returns the
// JSON body directly (no `.data` wrapper).
import { api } from '../utils/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  sources?: string[];
}

interface AdjusterQABotProps {
  reportId: string;
  isOpen: boolean;
  onToggle: () => void;
}

export default function AdjusterQABot({ reportId, isOpen, onToggle }: AdjusterQABotProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [followUps, setFollowUps] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversation history on mount
  useEffect(() => {
    if (reportId && isOpen) {
      loadHistory();
      loadSuggestions();
    }
  }, [reportId, isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get<{ messages?: Message[] }>(`/api/v1/adjuster-qa/history/${reportId}`);
      if (response?.messages) {
        setMessages(response.messages);
      }
    } catch (error) {
      console.log('No previous conversation history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const response = await api.get<{ suggestions?: string[] }>(`/api/v1/adjuster-qa/suggested-questions/${reportId}`);
      if (response?.suggestions) {
        setSuggestedQuestions(response.suggestions);
      }
    } catch (error) {
      console.log('Could not load suggestions');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setFollowUps([]);

    try {
      const response = await api.post<{
        answer: string;
        sources?: any[];
        follow_up_suggestions?: string[];
      }>('/api/v1/adjuster-qa/ask', {
        question: userMessage.content,
        report_id: reportId,
        conversation_history: messages.slice(-10)
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.answer,
        timestamp: new Date().toISOString(),
        sources: response.sources
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (response.follow_up_suggestions) {
        setFollowUps(response.follow_up_suggestions);
      }
    } catch (error: any) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message || 'Unknown error'}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await api.delete(`/api/v1/adjuster-qa/history/${reportId}`);
      setMessages([]);
      setFollowUps([]);
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Collapsed state - just show toggle button
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed right-0 top-1/2 -translate-y-1/2 p-3 rounded-l-lg shadow-lg transition-colors z-40"
        style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-brand)', border: '1px solid var(--color-border)', borderRight: 'none' }}
        title="Open AI Assistant"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="fixed right-0 top-0 h-full w-96 border-l shadow-xl z-40 flex flex-col" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-brand)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-on-brand)' }}>
          <Sparkles className="w-5 h-5" />
          <h2 className="font-semibold">AI Analysis Assistant</h2>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button
              onClick={handleClearHistory}
              className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onToggle}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
            title="Close"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loadingHistory ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <HelpCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <h3 className="font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Ask me anything about this analysis</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              I can explain findings, compare amounts, discuss gaps, and suggest improvements.
            </p>

            {/* Suggested Questions */}
            {suggestedQuestions.length > 0 && (
              <div className="text-left">
                <p className="text-xs font-medium mb-2 px-2" style={{ color: 'var(--color-text-muted)' }}>Suggested questions:</p>
                <div className="space-y-2">
                  {suggestedQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestion(q)}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg transition-colors"
                      style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className="max-w-[85%] rounded-lg px-4 py-2.5"
                  style={msg.role === 'user'
                    ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                    : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <BookOpen className="w-3 h-3" />
                        <span>Sources: {msg.sources.join(', ')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Follow-up Suggestions */}
            {followUps.length > 0 && !loading && (
              <div className="pt-2">
                <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>Follow-up questions:</p>
                <div className="flex flex-wrap gap-2">
                  {followUps.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestion(q)}
                      className="text-xs px-3 py-1.5 rounded-full transition-colors"
                      style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="rounded-lg px-4 py-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Analyzing...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about the analysis..."
            className="flex-1 px-4 py-2.5 border rounded-lg focus:outline-none focus:border-transparent text-sm"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="p-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
